import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Settings as SettingsIcon,
  Save,
  Building2,
  Receipt,
  ShieldCheck,
  Globe,
} from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { COUNTRIES, getCountry } from '@/lib/country';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { ReadOnlyNotice } from '@/components/ReadOnlyNotice';

export function SettingsPage() {
  const settings = useApp(s => s.settings);
  const updateSettings = useApp(s => s.updateSettings);
  const toast = useApp(s => s.toast);
  const t = useT();
  const [draft, setDraft] = useState(settings);
  // Einstellungen is one record: Lesen shows it, Bearbeiten saves it (#95).
  const readOnly = !useSectionRights()[SectionId.SETTINGS].edit;

  const set = <K extends keyof typeof draft>(k: K, v: (typeof draft)[K]) =>
    setDraft({ ...draft, [k]: v });

  const handleCountryChange = (code: string) => {
    const cc = getCountry(code);
    setDraft({
      ...draft,
      country: code,
      currency: cc.currency,
      vatPercent: cc.vatPercent,
      rounding: cc.rounding,
    });
  };

  const handleSave = () => {
    updateSettings(draft);
    toast(t('common.save'));
  };

  const country = getCountry(draft.country);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-primary" />{' '}
            {t('settings.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('settings.subtitle')}
          </p>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={handleSave}>
            <Save className="h-4 w-4" />
            {t('common.save')}
          </Button>
        )}
      </div>

      {readOnly && <ReadOnlyNotice />}

      {/* A disabled fieldset locks every field at once. */}
      <fieldset
        disabled={readOnly}
        className="grid min-w-0 gap-4 sm:grid-cols-2"
      >
        {/* Country / Region */}
        <Card className="sm:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Globe className="h-4 w-4" /> {t('common.country')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>{t('common.country')}</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={draft.country}
                  onChange={e => handleCountryChange(e.target.value)}
                >
                  {COUNTRIES.map(c => (
                    <option
                      key={c.code}
                      value={c.code}
                      className="bg-background text-foreground"
                    >
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {country.region === 'eu'
                    ? 'EU'
                    : country.region === 'us'
                      ? 'US'
                      : country.code}{' '}
                  · {country.taxLabel} · {country.currency}
                </p>
              </div>
              <div>
                <Label>{country.vatNumberLabel}</Label>
                <Input
                  value={draft.vatNumber}
                  onChange={e => set('vatNumber', e.target.value)}
                  placeholder={`${country.vatNumberPrefix}...`}
                />
              </div>
              <div>
                <Label>{country.taxLabel} %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={draft.vatPercent}
                  onChange={e => set('vatPercent', Number(e.target.value))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4" /> {t('settings.company')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t('settings.companyName')}</Label>
              <Input
                value={draft.companyName}
                onChange={e => set('companyName', e.target.value)}
              />
            </div>
            <div>
              <Label>{t('settings.street')}</Label>
              <Input
                value={draft.street}
                onChange={e => set('street', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label>{t('settings.zip')}</Label>
                <Input
                  value={draft.zip}
                  onChange={e => set('zip', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Label>{t('settings.city')}</Label>
                <Input
                  value={draft.city}
                  onChange={e => set('city', e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>{t('settings.phone')}</Label>
              <Input
                value={draft.phone}
                onChange={e => set('phone', e.target.value)}
              />
            </div>
            <div>
              <Label>{t('settings.email')}</Label>
              <Input
                value={draft.email}
                onChange={e => set('email', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Receipt className="h-4 w-4" /> {t('settings.financial')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>{t('settings.currency')}</Label>
                <Input
                  value={draft.currency}
                  onChange={e => set('currency', e.target.value)}
                  placeholder="EUR, CHF, USD…"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('settings.currencyHint')}
                </p>
              </div>
              <div>
                <Label>{t('settings.rounding')}</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={draft.rounding}
                  onChange={e => set('rounding', e.target.value)}
                >
                  {['0.01', '0.05', '0.10', '0.25', '0.50', '1.00'].map(r => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>{t('settings.iban')}</Label>
              <Input
                value={draft.iban}
                onChange={e => set('iban', e.target.value)}
                placeholder="CH56 0483 5012 3456 7800 9"
              />
            </div>
            <div>
              <Label>{t('settings.paymentTermsDays')}</Label>
              <Input
                type="number"
                value={draft.paymentTerms}
                onChange={e => set('paymentTerms', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>{t('settings.travelSurcharge')}</Label>
              <Input
                type="number"
                value={draft.travelSurcharge}
                onChange={e => set('travelSurcharge', Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="sm:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />{' '}
              {t('settings.inspectionRules')}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
            <div>
              <Label>{t('settings.inspectionInterval')}</Label>
              <Input
                type="number"
                value={draft.inspectionInterval}
                onChange={e =>
                  set('inspectionInterval', Number(e.target.value))
                }
              />
            </div>
            <div>
              <Label>{t('settings.boostAgeLimit')}</Label>
              <Input
                type="number"
                value={draft.boostAgeLimit}
                onChange={e => set('boostAgeLimit', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>{t('settings.permanentAgeLimit')}</Label>
              <Input
                type="number"
                value={draft.permanentAgeLimit}
                onChange={e => set('permanentAgeLimit', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>{t('settings.hoseTestInterval')}</Label>
              <Input
                type="number"
                value={draft.hoseTestInterval}
                onChange={e => set('hoseTestInterval', Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>
      </fieldset>
    </motion.div>
  );
}
